import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { format, differenceInDays, isValid } from 'date-fns';
import {
  Calendar,
  Clock,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Agreement } from '@/types/models';

interface ViewAgreementDialogProps {
  agreement: Agreement | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewAgreementDialog({
  agreement,
  open,
  onOpenChange,
}: ViewAgreementDialogProps) {
  if (!agreement) return null;

  // Helper function to get agreement status
  const getAgreementStatus = (agreement: Agreement) => {
    const today = new Date();
    const startDate = new Date(agreement.startDate);
    const endDate = new Date(agreement.endDate);
    const daysUntilExpiry = differenceInDays(endDate, today);
    const daysUntilStart = differenceInDays(startDate, today);
    const notifyDays = parseInt(agreement.notifyPeriodInDays || '0', 10);

    // Check if agreement hasn't started yet
    if (daysUntilStart > 0) {
      return {
        status: 'upcoming',
        label: 'Upcoming',
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: <Clock className="h-4 w-4" />
      };
    }

    // Check if agreement has expired
    if (daysUntilExpiry < 0) {
      return {
        status: 'expired',
        label: 'Expired',
        color: 'bg-red-50 text-red-700 border-red-200',
        icon: <XCircle className="h-4 w-4" />
      };
    }

    // Check if agreement is expiring soon (within 7 days or notify period)
    if (daysUntilExpiry <= Math.min(7, notifyDays)) {
      return {
        status: 'expiring_soon',
        label: 'Expiring Soon',
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        icon: <AlertCircle className="h-4 w-4" />
      };
    }

    // Check if agreement is in warning period (within 30 days or notify period * 2)
    if (daysUntilExpiry <= Math.min(30, notifyDays * 2)) {
      return {
        status: 'warning',
        label: 'Warning',
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        icon: <AlertTriangle className="h-4 w-4" />
      };
    }

    // If none of the above, it's active
    return {
      status: 'active',
      label: 'Active',
      color: 'bg-green-50 text-green-700 border-green-200',
      icon: <CheckCircle className="h-4 w-4" />
    };
  };

  const statusInfo = getAgreementStatus(agreement);
  const daysUntilExpiry = differenceInDays(new Date(agreement.endDate), new Date());

  const formatDate = (date: any) => {
    if (date && isValid(new Date(date))) {
      return format(new Date(date), "dd/MM/yyyy");
    }
    return "Not specified";
  };



  const renderInfoItem = (
    icon: React.ReactNode,
    label: string,
    value: string,
    iconColor: string = "text-blue-600"
  ) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <div className={iconColor}>{icon}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
          <p className="font-semibold text-gray-900 text-sm leading-tight">
            {value === "Not provided" || value === "Not specified" ? (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                {value}
              </span>
            ) : (
              value
            )}
          </p>
        </div>
      </div>
    </div>
  );

  const renderSectionHeader = (
    icon: React.ReactNode,
    title: string,
    _bgColor: string,
    iconBgColor: string,
    iconColor: string
  ) => (
    <div className={`flex items-center gap-3 mb-6`}>
      <div className={`w-10 h-10 ${iconBgColor} rounded-xl flex items-center justify-center`}>
        <div className={iconColor}>{icon}</div>
      </div>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] h-auto flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="px-4 pb-0">
          <DialogTitle className="sr-only">
            Agreement Details - {agreement.employee.user.firstName} {agreement.employee.user.lastName}
          </DialogTitle>
          <DialogDescription className="sr-only">
            View complete agreement information including employee details, agreement terms, and status information.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Employee Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {agreement.employee.user.firstName[0]}
                  {agreement.employee.user.lastName[0]}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 justify-between">

                    <h1 className="text-3xl font-bold text-gray-900">
                      {agreement.employee.user.firstName} {agreement.employee.user.lastName}
                    </h1>
                    <Badge
                      variant="outline"
                      className={`text-xs ${statusInfo.color}`}
                    >
                      <span className="flex items-center gap-1">
                        {statusInfo.icon}
                        {statusInfo.label}
                      </span>
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mb-2">

                    <span className="text-sm text-gray-600">{agreement.employee.user.email}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">

                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {daysUntilExpiry < 0 ? (
                        <span className="text-red-600 font-medium">
                          Expired {Math.abs(daysUntilExpiry)} days ago
                        </span>
                      ) : (
                        <span>
                          {daysUntilExpiry} days remaining
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>


            {/* Agreement Details */}
            <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100">
              {renderSectionHeader(
                <Calendar className="h-5 w-5" />,
                "Agreement Details",
                "bg-purple-50",
                "bg-purple-100",
                "text-purple-600"
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "Start Date",
                  formatDate(agreement.startDate),
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "End Date",
                  formatDate(agreement.endDate),
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Duration",
                  `${agreement.periodInMonths} months`,
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Notification Period",
                  `${agreement.notifyPeriodInDays} days`,
                  "text-purple-600"
                )}

                {agreement.note && (
                  <div className="col-span-full">
                    {renderInfoItem(
                      <Info className="h-4 w-4" />,
                      "Notes",
                      agreement.note,
                      "text-purple-600"
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
